// src/stores/InvoiceStore.js - Zustand Store replacing InvoiceSocketService + InvoicesProvider
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import SOCKET_EVENTS from '../services/socket/socketEvents';

// Store creation with selector subscription middleware
const useInvoiceStore = create(
  subscribeWithSelector((set, get) => ({
    // ================================
    // STATE (mirrors InvoicesProvider exactly)
    // ================================
    
    // Main invoices data
    invoices: [],
    invoicesLoading: false,
    invoicesPagination: null,

    // Selected invoice state (for InvoiceDetails/Builder page)
    selectedInvoice: null,
    selectedInvoiceLoading: false,
    
    // Invoice summary/stats (financial metrics)
    invoiceStats: {
      totalRevenue: 0,
      pendingAmount: 0,
      overdueAmount: 0,
      totalInvoices: 0,
      paidInvoices: 0,
      pendingInvoices: 0,
      overdueInvoices: 0
    },
    
    // Connection state
    isConnected: false,
    
    // Cache state (from InvoiceSocketService)
    cache: {
      invoices: [],
      pagination: null,
      selectedInvoice: null,
      recentInvoices: [],
      invoiceStats: null,
      lastFetch: null
    },
    
    // Internal flags
    _initialized: false,
    
    // ================================
    // SOCKET OPERATIONS (from InvoiceSocketService)
    // ================================
    
    /**
     * Request paginated list of invoices - NO RETURN VALUE
     * @param {object} options - Pagination and filtering options
     */
    loadInvoices: (options = {}) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ InvoiceStore: Socket service not initialized');
        return;
      }
      
      const {
        page = 1,
        limit = 50,
        statusFilter = null,      // draft, sent, paid, overdue
        dateFilter = 'all',       // all, this_month, last_month, overdue
        search = '',
        sortBy = 'invoice_date',  // invoice_date, total_amount, customer_name
        sortOrder = 'desc'        // asc, desc
      } = options;

      console.log('💰 InvoiceStore: Loading invoices with options:', { page, limit, statusFilter, dateFilter, search });
      
      set({ invoicesLoading: true });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_INVOICES, {
        page,
        limit,
        status_filter: statusFilter,
        date_filter: dateFilter,
        search,
        sort_by: sortBy,
        sort_order: sortOrder
      });
    },
    
    /**
     * Request detailed invoice information - NO RETURN VALUE
     * @param {string} invoiceId - Invoice ID
     */
    loadInvoiceDetails: (invoiceId) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ InvoiceStore: Socket service not initialized');
        return;
      }
      
      console.log('📄 InvoiceStore: Loading invoice details:', invoiceId);
      
      set({ selectedInvoiceLoading: true });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_INVOICE_DETAILS, {
        invoice_id: invoiceId
      });
    },
    
    /**
     * Request invoice summary/stats - NO RETURN VALUE
     * @param {string} period - current_month, last_month, ytd, all_time
     * @param {boolean} includeCharts - Whether to include chart data
     */
    loadInvoiceSummary: (period = 'current_month', includeCharts = false) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ InvoiceStore: Socket service not initialized');
        return;
      }
      
      console.log('📊 InvoiceStore: Loading invoice summary:', period);
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_INVOICE_SUMMARY, {
        period,
        include_charts: includeCharts
      });
    },
    
    /**
     * Request to create a new invoice - NO RETURN VALUE
     * @param {object} invoiceData - Invoice information
     */
    createInvoice: (invoiceData) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ InvoiceStore: Socket service not initialized');
        return;
      }
      
      console.log('➕ InvoiceStore: Creating invoice for customer:', invoiceData.customer_id);
      
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.CREATE_INVOICE, {
        customer_id: invoiceData.customer_id,
        invoice_date: invoiceData.invoice_date || new Date().toISOString().split('T')[0],
        due_date: invoiceData.due_date,
        payment_terms: invoiceData.payment_terms || 'Due on receipt',
        notes: invoiceData.notes || '',
        line_items: invoiceData.line_items,
        event_ids: invoiceData.event_ids || [] // Link to appointments
      });
    },
    
    /**
     * Request to update invoice information - NO RETURN VALUE
     * @param {string} invoiceId - Invoice ID
     * @param {object} updateData - Fields to update
     */
    updateInvoice: (invoiceId, updateData) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ InvoiceStore: Socket service not initialized');
        return;
      }
      
      console.log('✏️ InvoiceStore: Updating invoice:', invoiceId, updateData);
      
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.UPDATE_INVOICE, {
        invoice_id: invoiceId,
        ...updateData
      });
    },
    
    /**
     * Request to delete an invoice - NO RETURN VALUE
     * @param {string} invoiceId - Invoice ID
     */
    deleteInvoice: (invoiceId) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ InvoiceStore: Socket service not initialized');
        return;
      }
      
      console.log('🗑️ InvoiceStore: Deleting invoice:', invoiceId);
      
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.DELETE_INVOICE, {
        invoice_id: invoiceId
      });
    },
    
    /**
     * Request to send invoice to customer - NO RETURN VALUE
     * @param {string} invoiceId - Invoice ID
     * @param {string} sendMethod - email, sms, both
     * @param {string} customMessage - Optional custom message
     */
    sendInvoice: (invoiceId, sendMethod = 'email', customMessage = '') => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ InvoiceStore: Socket service not initialized');
        return;
      }
      
      console.log('📧 InvoiceStore: Sending invoice:', invoiceId, sendMethod);
      
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.SEND_INVOICE, {
        invoice_id: invoiceId,
        send_method: sendMethod,
        custom_message: customMessage
      });
    },

    /**
     * Request to create and send invoice atomically - NO RETURN VALUE
     * @param {object} invoiceData - Invoice information
     * @param {string} sendMethod - email, sms, both
     * @param {string} customMessage - Optional custom message
     */
    createAndSendInvoice: (invoiceData, sendMethod = 'email', customMessage = '') => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ InvoiceStore: Socket service not initialized');
        return;
      }
      
      console.log('🚀 InvoiceStore: Creating and sending invoice for customer:', invoiceData.customer_id);
      
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.CREATE_AND_SEND_INVOICE, {
        customer_id: invoiceData.customer_id,
        invoice_date: invoiceData.invoice_date || new Date().toISOString().split('T')[0],
        due_date: invoiceData.due_date,
        payment_terms: invoiceData.payment_terms || 'Due on receipt',
        notes: invoiceData.notes || '',
        line_items: invoiceData.line_items,
        event_ids: invoiceData.event_ids || [],
        send_method: sendMethod,
        custom_message: customMessage
      });
    },
    
    /**
     * Request to record a manual payment - NO RETURN VALUE
     * @param {string} invoiceId - Invoice ID
     * @param {object} paymentData - Payment information
     */
    recordPayment: (invoiceId, paymentData) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ InvoiceStore: Socket service not initialized');
        return;
      }
      
      console.log('💳 InvoiceStore: Recording payment:', invoiceId, paymentData);
      
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.MARK_INVOICE_PAID, {
        invoice_id: invoiceId,
        payment_amount: paymentData.payment_amount,
        payment_method: paymentData.payment_method,
        payment_date: paymentData.payment_date || new Date().toISOString().split('T')[0],
        payment_notes: paymentData.payment_notes || ''
      });
    },
    
    /**
     * Request to update payment status - NO RETURN VALUE
     * @param {string} invoiceId - Invoice ID
     * @param {string} paymentStatus - paid, partially_paid, failed
     * @param {object} paymentDetails - Payment details from processor
     */
    updatePaymentStatus: (invoiceId, paymentStatus, paymentDetails = {}) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ InvoiceStore: Socket service not initialized');
        return;
      }
      
      console.log('🔄 InvoiceStore: Updating payment status:', invoiceId, paymentStatus);
      
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.UPDATE_PAYMENT_STATUS, {
        invoice_id: invoiceId,
        payment_status: paymentStatus,
        payment_details: paymentDetails
      });
    },
    
    // ================================
    // LINE ITEM OPERATIONS
    // ================================
    
    /**
     * Request to add a line item - NO RETURN VALUE
     * @param {string} invoiceId - Invoice ID
     * @param {object} lineItemData - Line item information
     */
    addLineItem: (invoiceId, lineItemData) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ InvoiceStore: Socket service not initialized');
        return;
      }
      
      console.log('➕ InvoiceStore: Adding line item to invoice:', invoiceId, lineItemData);
      
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.ADD_LINE_ITEM, {
        invoice_id: invoiceId,
        description: lineItemData.description,
        quantity: lineItemData.quantity,
        unit_price: lineItemData.unit_price,
        line_item_type: lineItemData.line_item_type || 'service'
      });
    },
    
    /**
     * Request to update a line item - NO RETURN VALUE
     * @param {string} lineItemId - Line item ID
     * @param {object} updateData - Fields to update
     */
    updateLineItem: (lineItemId, updateData) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ InvoiceStore: Socket service not initialized');
        return;
      }
      
      console.log('✏️ InvoiceStore: Updating line item:', lineItemId, updateData);
      
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.UPDATE_LINE_ITEM, {
        line_item_id: lineItemId,
        ...updateData
      });
    },
    
    /**
     * Request to remove a line item - NO RETURN VALUE
     * @param {string} lineItemId - Line item ID
     */
    removeLineItem: (lineItemId) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ InvoiceStore: Socket service not initialized');
        return;
      }
      
      console.log('🗑️ InvoiceStore: Removing line item:', lineItemId);
      
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.REMOVE_LINE_ITEM, {
        line_item_id: lineItemId
      });
    },
    
    /**
     * Request to reorder line items - NO RETURN VALUE
     * @param {string} invoiceId - Invoice ID
     * @param {Array} newOrder - Array of line item IDs in new order
     */
    reorderLineItems: (invoiceId, newOrder) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ InvoiceStore: Socket service not initialized');
        return;
      }
      
      console.log('🔄 InvoiceStore: Reordering line items:', invoiceId, newOrder);
      
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.REORDER_LINE_ITEMS, {
        invoice_id: invoiceId,
        new_order: newOrder
      });
    },
    
    // ================================
    // UTILITY FUNCTIONS (from InvoicesProvider)
    // ================================
    
    clearSelectedInvoice: () => {
      set({ selectedInvoice: null });
    },
    
    refreshInvoices: (options = {}) => {
      // Clear cache first
      set(state => ({
        cache: {
          ...state.cache,
          invoices: [],
          pagination: null,
          selectedInvoice: null,
          lastFetch: null
        }
      }));
      
      // Then reload with force option
      get().loadInvoices({ ...options, force: true });
    },
    
    getInvoiceById: (invoiceId) => {
      const { invoices } = get();
      return invoices.find(inv => inv.id === invoiceId) || null;
    },
    
    // Get invoices for specific customer
    getCustomerInvoices: (customerId) => {
      const { invoices } = get();
      return invoices.filter(invoice => invoice.customer_id === customerId);
    },
    
    // Calculate invoice totals (client-side validation)
    calculateTotals: (lineItems, taxRate = 0) => {
      const subtotal = lineItems.reduce((sum, item) => {
        return sum + (item.quantity * item.unit_price);
      }, 0);
      
      const taxAmount = subtotal * taxRate;
      const total = subtotal + taxAmount;
      
      return {
        subtotal: parseFloat(subtotal.toFixed(2)),
        tax_amount: parseFloat(taxAmount.toFixed(2)),
        total_amount: parseFloat(total.toFixed(2))
      };
    },
    
    // Get overdue invoices from cache
    getOverdueInvoices: () => {
      const { invoices } = get();
      const today = new Date().toISOString().split('T')[0];
      return invoices.filter(invoice => 
        invoice.payment_status !== 'paid' && 
        invoice.due_date && 
        invoice.due_date < today
      );
    },
    
    // ================================
    // COMPUTED VALUES (from InvoicesProvider)
    // ================================
    
    // Get recent invoices (last 30 days) - computed in hook
    getRecentInvoices: () => {
      const { invoices } = get();
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
      return invoices.filter(invoice => {
        const invoiceDate = new Date(invoice.invoice_date);
        return invoiceDate >= thirtyDaysAgo;
      });
    },
    
    // ================================
    // INTERNAL STATE UPDATERS (called by socket listeners)
    // ================================
    
    _setInvoicesLoaded: (data) => {
      console.log('📡 InvoiceStore: Invoices list received');
      
      set(state => ({
        invoices: data.invoices || [],
        invoicesPagination: data.pagination,
        invoiceStats: data.stats ? { ...state.invoiceStats, ...data.stats } : state.invoiceStats,
        invoicesLoading: false,
        cache: {
          ...state.cache,
          invoices: data.invoices || [],
          pagination: data.pagination,
          invoiceStats: data.stats || state.cache.invoiceStats,
          lastFetch: new Date()
        }
      }));
    },
    
    _setInvoiceDetailsLoaded: (data) => {
      // FIXED: Expect only flat structure - no more nested fallback
      const invoiceData = data;
      
      console.log('📡 InvoiceStore: Invoice details received:', invoiceData.invoice_number);
      
      set(state => {
        // Add/update in main invoices array with smart deduplication
        const existingIndex = state.invoices.findIndex(inv => inv.id === invoiceData.id);
        const updatedInvoices = existingIndex !== -1 
          ? state.invoices.map((inv, index) => 
              index === existingIndex ? { ...inv, ...invoiceData } : inv
            )
          : [invoiceData, ...state.invoices];
        
        return {
          invoices: updatedInvoices,
          selectedInvoice: invoiceData,
          selectedInvoiceLoading: false,
          cache: {
            ...state.cache,
            selectedInvoice: invoiceData
          }
        };
      });
    },
    
    _setInvoiceSummaryLoaded: (data) => {
      console.log('📡 InvoiceStore: Invoice summary received');
      
      set(state => ({
        invoiceStats: data.stats ? { ...state.invoiceStats, ...data.stats } : state.invoiceStats,
        cache: {
          ...state.cache,
          invoiceStats: data.stats
        }
      }));
    },
    
    _addInvoice: (invoiceData) => {
      console.log('📡 InvoiceStore: New invoice created:', invoiceData.invoice_number);
      
      set(state => {
        // Check for duplicates
        const exists = state.invoices.find(inv => inv.id === invoiceData.id);
        if (exists) return state;
        
        // Update stats for creation
        const newStats = { ...state.invoiceStats };
        newStats.totalInvoices += 1;
        if (invoiceData.status === 'paid') {
          newStats.paidInvoices += 1;
          newStats.totalRevenue += (invoiceData.total_amount || 0);
        } else if (invoiceData.status === 'sent') {
          newStats.pendingInvoices += 1;
          newStats.pendingAmount += (invoiceData.total_amount || 0);
        }
        
        return {
          invoices: [invoiceData, ...state.invoices],
          invoiceStats: newStats,
            cache: {
            ...state.cache,
            invoices: [invoiceData, ...state.cache.invoices]
          }
        };
      });
    },
    
    _updateInvoice: (invoiceData) => {
      console.log('📡 InvoiceStore: Invoice updated:', invoiceData.invoice_number);
      
      set(state => {
        // Update in main invoices array
        const updatedInvoices = state.invoices.map(inv => 
          inv.id === invoiceData.id ? { ...inv, ...invoiceData } : inv
        );
        
        // Update selected invoice if it's the same one
        const updatedSelectedInvoice = state.selectedInvoice?.id === invoiceData.id 
          ? { ...state.selectedInvoice, ...invoiceData }
          : state.selectedInvoice;
        
        return {
          invoices: updatedInvoices,
          selectedInvoice: updatedSelectedInvoice,
            cache: {
            ...state.cache,
            invoices: updatedInvoices,
            selectedInvoice: updatedSelectedInvoice
          }
        };
      });
    },

    _addInvoiceCreatedAndSent: (responseData) => {
      // FIXED: Expect flat structure directly - no more nested access
      const invoiceData = responseData;
      
      console.log('📡 InvoiceStore: Invoice created and sent:', invoiceData.invoice_number);
      
      set(state => {
        // Check for duplicates
        const exists = state.invoices.find(inv => inv.id === invoiceData.id);
        if (exists) return state;
        
        // Update stats for creation and sending
        const newStats = { ...state.invoiceStats };
        newStats.totalInvoices += 1;
        newStats.pendingInvoices += 1;
        newStats.pendingAmount += (invoiceData.total_amount || 0);
        
        return {
          invoices: [invoiceData, ...state.invoices],
          invoiceStats: newStats,
            cache: {
            ...state.cache,
            invoices: [invoiceData, ...state.cache.invoices]
          }
        };
      });
    },
    
    _removeInvoice: (invoiceId) => {
      console.log('📡 InvoiceStore: Invoice deleted:', invoiceId);
      
      set(state => {
        // Remove from main invoices array
        const updatedInvoices = state.invoices.filter(inv => inv.id !== invoiceId);
        
        // Clear selected invoice if it was deleted
        const updatedSelectedInvoice = state.selectedInvoice?.id === invoiceId 
          ? null 
          : state.selectedInvoice;
        
        // Update stats
        const newStats = { ...state.invoiceStats };
        newStats.totalInvoices -= 1;
        
        return {
          invoices: updatedInvoices,
          selectedInvoice: updatedSelectedInvoice,
          invoiceStats: newStats,
            cache: {
            ...state.cache,
            invoices: updatedInvoices,
            selectedInvoice: updatedSelectedInvoice
          }
        };
      });
    },
    
    _updateInvoiceFromPaymentChange: (invoiceData, eventName) => {
      console.log('📡 InvoiceStore: Payment status changed:', invoiceData.invoice_number);
      
      set(state => {
        // Update in main invoices array
        const updatedInvoices = state.invoices.map(inv => 
          inv.id === invoiceData.id ? { ...inv, ...invoiceData } : inv
        );
        
        // Update selected invoice if applicable
        const updatedSelectedInvoice = state.selectedInvoice?.id === invoiceData.id 
          ? { ...state.selectedInvoice, ...invoiceData }
          : state.selectedInvoice;
        
        // Update stats for payment
        const newStats = { ...state.invoiceStats };
        if (invoiceData.payment_status === 'paid') {
          newStats.totalRevenue += (invoiceData.payment_amount || invoiceData.total_amount || 0);
        }
        
        return {
          invoices: updatedInvoices,
          selectedInvoice: updatedSelectedInvoice,
          invoiceStats: newStats,
            cache: {
            ...state.cache,
            invoices: updatedInvoices,
            selectedInvoice: updatedSelectedInvoice
          }
        };
      });
    },
    
    _updateInvoiceFromLineItemChange: (invoiceData, eventName) => {
      console.log('📡 InvoiceStore: Line item changed for invoice:', invoiceData.invoice_number);
      
      set(state => {
        // Update invoice totals in main array
        const updatedInvoices = state.invoices.map(inv => 
          inv.id === invoiceData.id ? { ...inv, ...invoiceData } : inv
        );
        
        // Update selected invoice with full line item data
        const updatedSelectedInvoice = state.selectedInvoice?.id === invoiceData.id 
          ? { ...state.selectedInvoice, ...invoiceData }
          : state.selectedInvoice;
        
        return {
          invoices: updatedInvoices,
          selectedInvoice: updatedSelectedInvoice,
          cache: {
            ...state.cache,
            invoices: updatedInvoices,
            selectedInvoice: updatedSelectedInvoice
          }
        };
      });
    },

    // ================================
    // INTERNAL METHODS (socket manager, initialization)
    // ================================
    
    _getSocketManager: () => {
      // Access socket manager from window global
      return { 
        socketManager: typeof window !== 'undefined' ? window.__socketManager : null 
      };
    },
    
  _initialize: (socketManager) => {
    console.log('🔌 InvoiceStore: Initializing with socket manager');
    
    if (typeof window !== 'undefined') {
      window.__socketManager = socketManager;
    }
    
    set({ 
      isConnected: true,
      _initialized: true 
    });
    
    // Always auto-load invoices on initialization
    console.log('🔄 InvoiceStore: Auto-loading invoices (first time)');
    get().loadInvoices({
      page: 1,
      limit: 50,
      dateFilter: 'all'
    });
  },
    
    _setConnectionState: (connected) => {
      set({ isConnected: connected });
    },
    
    _handleAuthReset: () => {
      console.log('🧹 InvoiceStore: Resetting all state due to auth reset');
      
      set({
        // Reset all state to initial values
        invoices: [],
        invoicesLoading: false,
        invoicesPagination: null,
        selectedInvoice: null,
        selectedInvoiceLoading: false,
        invoiceStats: {
          totalRevenue: 0,
          pendingAmount: 0,
          overdueAmount: 0,
          totalInvoices: 0,
          paidInvoices: 0,
          pendingInvoices: 0,
          overdueInvoices: 0
        },
        isConnected: false,
        cache: {
          invoices: [],
          pagination: null,
          selectedInvoice: null,
          recentInvoices: [],
          invoiceStats: null,
          lastFetch: null
        },
        _initialized: false
      });
    }
  }))
);

// ================================
// SOCKET EVENT LISTENERS SETUP (Global initialization)
// ================================


export const initializeInvoiceStoreSocketListeners = (socketManager) => {
  if (!socketManager?.universalHandler) {
    return;
  }
  
  console.log('👂 InvoiceStore: Setting up socket event listeners');
  
  const { universalHandler } = socketManager;
  
  // Set up universal response listener for all invoice events
  const handleSocketResponse = (responseData) => {
    const eventName = responseData.event;
    const store = useInvoiceStore.getState();
    
    switch (eventName) {
      // ✅ SUCCESS EVENTS
      case SOCKET_EVENTS.INVOICES_LOADED:
        store._setInvoicesLoaded(responseData.data);
        break;
        
      case SOCKET_EVENTS.INVOICE_DETAILS_LOADED:
        store._setInvoiceDetailsLoaded(responseData.data);
        break;
        
      case SOCKET_EVENTS.INVOICE_SUMMARY_LOADED:
        store._setInvoiceSummaryLoaded(responseData.data);
        break;

      case SOCKET_EVENTS.INVOICE_CREATED_AND_SENT:
        store._addInvoiceCreatedAndSent(responseData.data);
        break;
        
      case SOCKET_EVENTS.INVOICE_CREATED:
        // FIXED: Expect flat structure only - no more defensive nested handling
        store._addInvoice(responseData.data);
        break;
        
      case SOCKET_EVENTS.INVOICE_UPDATED:
        store._updateInvoice(responseData.data);
        break;
        
      case SOCKET_EVENTS.INVOICE_DELETED:
        store._removeInvoice(responseData.data.invoice_id);
        break;
        
      case SOCKET_EVENTS.INVOICE_SENT:
        store._updateInvoice(responseData.data);
        break;
        
      case SOCKET_EVENTS.INVOICE_PAYMENT_RECORDED:
      case SOCKET_EVENTS.INVOICE_PAYMENT_STATUS_UPDATED:
        store._updateInvoiceFromPaymentChange(responseData.data, eventName);
        break;
        
      case SOCKET_EVENTS.INVOICE_LINE_ITEM_ADDED:
      case SOCKET_EVENTS.INVOICE_LINE_ITEM_UPDATED:
      case SOCKET_EVENTS.INVOICE_LINE_ITEM_REMOVED:
      case SOCKET_EVENTS.INVOICE_LINE_ITEMS_REORDERED:
        store._updateInvoiceFromLineItemChange(responseData.data, eventName);
        break;
        
      // ❌ ERROR EVENTS - NotificationListener handles display
      case SOCKET_EVENTS.GET_INVOICES_FAILED:
      case SOCKET_EVENTS.GET_INVOICE_SUMMARY_FAILED:
        set({ invoicesLoading: false });
        break;

      case SOCKET_EVENTS.GET_INVOICE_DETAILS_FAILED:
        set({ selectedInvoiceLoading: false });
        break;

      case SOCKET_EVENTS.CREATE_INVOICE_FAILED:
      case SOCKET_EVENTS.UPDATE_INVOICE_FAILED:
      case SOCKET_EVENTS.DELETE_INVOICE_FAILED:
      case SOCKET_EVENTS.SEND_INVOICE_FAILED:
      case SOCKET_EVENTS.CREATE_AND_SEND_INVOICE_FAILED:
      case SOCKET_EVENTS.ADD_LINE_ITEM_FAILED:
      case SOCKET_EVENTS.UPDATE_LINE_ITEM_FAILED:
      case SOCKET_EVENTS.REMOVE_LINE_ITEM_FAILED:
      case SOCKET_EVENTS.REORDER_LINE_ITEMS_FAILED:
      case SOCKET_EVENTS.MARK_INVOICE_PAID_FAILED:
      case SOCKET_EVENTS.UPDATE_PAYMENT_STATUS_FAILED:
        // Just log - NotificationListener shows error to user
        console.log('❌ InvoiceStore: Operation failed -', responseData.message);
        break;
        
      default:
        // Not an invoice-related event, ignore
        break;
    }
  };
  
  // Set up individual event listeners using existing system
  const invoiceEvents = [
    // Success events
    SOCKET_EVENTS.INVOICES_LOADED,
    SOCKET_EVENTS.INVOICE_DETAILS_LOADED,
    SOCKET_EVENTS.INVOICE_SUMMARY_LOADED,
    SOCKET_EVENTS.INVOICE_CREATED_AND_SENT,
    SOCKET_EVENTS.INVOICE_CREATED,
    SOCKET_EVENTS.INVOICE_UPDATED,
    SOCKET_EVENTS.INVOICE_DELETED,
    SOCKET_EVENTS.INVOICE_SENT,
    SOCKET_EVENTS.INVOICE_PAYMENT_RECORDED,
    SOCKET_EVENTS.INVOICE_PAYMENT_STATUS_UPDATED,
    SOCKET_EVENTS.INVOICE_LINE_ITEM_ADDED,
    SOCKET_EVENTS.INVOICE_LINE_ITEM_UPDATED,
    SOCKET_EVENTS.INVOICE_LINE_ITEM_REMOVED,
    SOCKET_EVENTS.INVOICE_LINE_ITEMS_REORDERED,
    // Error events
    SOCKET_EVENTS.GET_INVOICES_FAILED,
    SOCKET_EVENTS.GET_INVOICE_DETAILS_FAILED,
    SOCKET_EVENTS.GET_INVOICE_SUMMARY_FAILED,
    SOCKET_EVENTS.CREATE_INVOICE_FAILED,
    SOCKET_EVENTS.CREATE_AND_SEND_INVOICE_FAILED, 
    SOCKET_EVENTS.UPDATE_INVOICE_FAILED,
    SOCKET_EVENTS.DELETE_INVOICE_FAILED,
    SOCKET_EVENTS.SEND_INVOICE_FAILED,
    SOCKET_EVENTS.ADD_LINE_ITEM_FAILED,
    SOCKET_EVENTS.UPDATE_LINE_ITEM_FAILED,
    SOCKET_EVENTS.REMOVE_LINE_ITEM_FAILED,
    SOCKET_EVENTS.REORDER_LINE_ITEMS_FAILED,
    SOCKET_EVENTS.MARK_INVOICE_PAID_FAILED,
    SOCKET_EVENTS.UPDATE_PAYMENT_STATUS_FAILED
  ];
  
  // Set up individual event listeners using existing system
  invoiceEvents.forEach(eventName => {
    universalHandler.on(eventName, (data) => {
      handleSocketResponse({ event: eventName, data });
    });
  });
  
  // Set up auth reset listener
  if (typeof window !== 'undefined') {
    window.addEventListener('auth-state-reset', () => {
      useInvoiceStore.getState()._handleAuthReset();
    });
  }
  
  // Initialize the store
  useInvoiceStore.getState()._initialize(socketManager);
  
  console.log('✅ InvoiceStore: Socket event listeners initialized');
};

export default useInvoiceStore;